import React from 'react';
import { DirectionalHint, IContextualMenuProps, useTheme } from '@fluentui/react';
import { ChevronDown12Regular as ChevronIcon } from '@fluentui/react-icons';
import { ContextualMenuDrawer } from 'common/components/Drawer/ContextualMenuDrawer';
import usePlatform from 'common/hooks/usePlatform';
import { IAppTheme } from 'common/theme/AppTheme';
import { mergeWith } from 'lodash';
import { useAppSelector } from 'store/hooks';

import { BaseButton } from '../BaseButton';
import { IBaseButtonProps } from '../BaseButton.types';
import { IconButton } from '../IconButton';

import IOSMenuButton from './IOSMenuButton';
import { getIconClassName, getStyles } from './MenuButton.styles';

export interface IMenuButtonProps extends IBaseButtonProps {
  menuProps: IContextualMenuProps;
  hideChevron?: boolean;
}

const defaultMenuProps: Partial<IContextualMenuProps> = {
  isBeakVisible: true,
  directionalHint: DirectionalHint.rightCenter,
  gapSpace: 10,
};

/**
 * IMPORTANT NOTE: This component creates a layer with a focus trap.
 * If you are using this in a modal/dialog to conditionally
 * render content in the same modal (for example: RecommendationDialog),
 * you must manage focus after selecting a menu option to maintain
 * keyboard accessibility (i.e., move focus to a specific element in the
 * modal after selecting a menu option), otherwise focus will be lost behind
 * the modal. This is because the menu's layer interferes with the
 * modal/dialog layer.
 *
 * @param props IMenuButtonProps
 * @returns {React.FunctionComponent} A button that opens a floating menu.
 */
const MenuButton: React.FunctionComponent<IMenuButtonProps> = (props) => {
  const { menuProps, hideChevron, styles, ...otherProps } = props;
  const theme = useTheme() as IAppTheme;
  const customStyles = getStyles(styles, theme, otherProps.componentAs === IconButton);
  const iconClassName = getIconClassName();
  const { isBlurryBackground } = useAppSelector((state) => state.layout);
  const { isIOSDevice } = usePlatform();
  const MenuIcon = <ChevronIcon className={iconClassName} />;

  // Use iOS-specific implementation for better voice control support
  if (isIOSDevice) {
    return <IOSMenuButton {...props} />;
  }

  const customCalloutProps: Partial<IContextualMenuProps> = isBlurryBackground
    ? {
        calloutProps: {
          styles: { root: { filter: 'brightness(0.7) blur(6px)' } },
        },
      }
    : {};

  return (
    <BaseButton
      styles={customStyles}
      allowDisabledFocus
      menuAs={ContextualMenuDrawer}
      menuProps={mergeWith({}, defaultMenuProps, menuProps, customCalloutProps)}
      onRenderMenuIcon={hideChevron || otherProps.isLoading ? undefined : () => MenuIcon}
      {...otherProps}
    />
  );
};

export default MenuButton;
